import moment, { Moment } from "moment-timezone";

export interface Session {
  date: Moment | Date | string;
  durationFinal: number;
}

export interface DailyData {
  date: string;
  day: string;
  duration: number;
}

export default class MonthlyActivity {
  /**
   * Process sessions data for monthly activity chart
   * Handles sessions that span multiple days by distributing duration across days
   * @param monthStart - Start of the month (moment or string)
   * @param monthEnd - End of the month (moment or string)
   * @param sessions - Array of session objects
   * @returns Array of daily data objects
   */
  static processData(
    monthStart: Moment | string,
    monthEnd: Moment | string,
    sessions: Session[]
  ): DailyData[] {
    const sumsByDate: Record<string, DailyData> = {};
    const startMoment = moment.isMoment(monthStart)
      ? monthStart
      : moment(monthStart);
    const endMoment = moment.isMoment(monthEnd) ? monthEnd : moment(monthEnd);

    // Initialize all days in the month range (including partial weeks)
    for (
      let m = startMoment.clone().startOf("month").startOf("week");
      m.isBefore(endMoment.clone().endOf("month").endOf("week"));
      m.add(1, "days")
    ) {
      sumsByDate[m.format("YYYY-MM-DD")] = {
        date: m.format("YYYY-MM-DD"),
        day: m.format("ddd"), // day of the week
        duration: 0,
      };
    }

    // Build [start, end] intervals in each session's OWN timezone. Cloning
    // preserves the pinned tz; moment(dateString) would reparse in the local tz
    // and misplace day boundaries for sessions tugged in another zone (which was
    // inflating days past 24h and spilling negatives onto the next day). Skip
    // in-progress (durationFinal === -1) and empty sessions.
    const intervals = sessions
      .filter((s) => s.durationFinal > 0)
      .map((s) => {
        const start = moment.isMoment(s.date) ? s.date.clone() : moment(s.date);
        return {
          start,
          end: start.clone().add(s.durationFinal, "milliseconds"),
        };
      })
      .sort((a, b) => a.start.valueOf() - b.start.valueOf());

    // Merge overlapping intervals so time covered by more than one session is
    // counted once. Sessions shouldn't overlap in healthy data (so this is a
    // no-op then), but duplicate/bad rows -- e.g. two ~32h sessions a minute
    // apart -- were being summed into impossible 93h days. "Hours tugged" is
    // wall-clock coverage, which can't exceed 24h/day.
    const merged: Array<{ start: Moment; end: Moment }> = [];
    for (const iv of intervals) {
      const last = merged[merged.length - 1];
      if (last && iv.start.isSameOrBefore(last.end)) {
        if (iv.end.isAfter(last.end)) last.end = iv.end;
      } else {
        merged.push({ start: iv.start.clone(), end: iv.end.clone() });
      }
    }

    // Split each merged span across the days it covers, clipping to each day so
    // a day can never exceed 24h and the pieces sum back to the span.
    merged.forEach(({ start, end }) => {
      for (
        let cursor = start.clone().startOf("day");
        cursor.isSameOrBefore(end, "day");
        cursor.add(1, "day")
      ) {
        const key = cursor.format("YYYY-MM-DD");
        if (!sumsByDate[key]) continue; // outside the loaded month grid

        const ms = moment
          .min(end, cursor.clone().add(1, "day"))
          .diff(moment.max(start, cursor.clone()), "milliseconds");
        if (ms > 0) sumsByDate[key].duration += ms;
      }
    });

    return Object.values(sumsByDate);
  }

  /**
   * Group daily data by week
   * @param data - Array of daily data objects
   * @returns Object mapping week keys to arrays of data points
   */
  static groupByWeek(
    data: DailyData[]
  ): Record<string, Array<{ x: string; y: number; date: string }>> {
    return data.reduce((acc, curr) => {
      const week = moment(curr.date).startOf("week").format("YYYY-MM-DD");
      if (!acc[week]) {
        acc[week] = [];
      }
      acc[week].push({
        x: curr.day,
        y: Math.max(curr.duration, 0),
        date: curr.date,
      });
      return acc;
    }, {} as Record<string, Array<{ x: string; y: number; date: string }>>);
  }

  /**
   * Format week label for chart
   * @param weekKey - Week key (YYYY-MM-DD format)
   * @returns Formatted week label (e.g., "Jan 15")
   */
  static formatWeekLabel(weekKey: string): string {
    return moment(weekKey).format("MMM DD");
  }
}
